// External link tracking utility

import { activityApi } from './api'

/**
 * Track an external link click
 * @param url - The external URL being clicked
 * @param linkName - Human-readable name for the link (e.g., "Grafana", "HiveMQ Documentation")
 * @param pagePath - Optional current page path (defaults to window.location.pathname)
 * @param pageTitle - Optional current page title (defaults to document.title)
 */
export async function trackExternalLink(
  url: string,
  linkName: string,
  pagePath?: string,
  pageTitle?: string,
): Promise<void> {
  try {
    await activityApi.logExternalLink({
      pagePath: pagePath || window.location.pathname,
      pageTitle: pageTitle || document.title,
      externalUrl: url,
      linkName,
    })
  } catch (error) {
    // Silently fail - don't break the app if tracking fails
    console.debug('External link tracking failed:', error)
  }
}

/**
 * Create an onClick handler that tracks the click and opens the link
 * @param url - The external URL
 * @param linkName - Human-readable name for the link
 * @param openInNewTab - Whether to open in new tab (default: true)
 */
export function createTrackedLinkHandler(
  url: string,
  linkName: string,
  openInNewTab = true,
): (e?: React.MouseEvent) => void {
  return (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault()
    }

    // Track the click (fire and forget)
    trackExternalLink(url, linkName)

    // Open the link
    if (openInNewTab) {
      window.open(url, '_blank', 'noopener,noreferrer')
    } else {
      window.location.href = url
    }
  }
}
